import { useState } from 'react';
import { Box, Chip, Divider, IconButton, ListItemIcon, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { BoardDetailResponse, BoardSummaryResponse } from '../../services/responses/board.response';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';
import {
    DEFAULT_BADGE_TINT, INTAKE_BADGE_TINT, badgeSx, boardItemSx, createButtonSx, pickerColors, pickerPaperSx,
    searchFieldSx, toggleActiveButtonSx,
} from './crm-board-picker.styles';

interface Props {
    anchorEl: HTMLElement | null;
    summaries: BoardSummaryResponse[];
    selectedBoardId: string;
    board: BoardDetailResponse | null;
    onClose: () => void;
    onSelect: (boardId: string) => void;
    onEdit: (summary: BoardSummaryResponse) => void;
    onDelete: (boardId: string) => void;
    onCreate: () => void;
    onToggleActive: () => void;
}

function formatRelativeUpdate(isoDate: string, t: TFunction): string {
    const updated = new Date(isoDate);
    const today = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfDay(today) - startOfDay(updated)) / 86400000);

    if (diffDays <= 0) return t('crmKanban.atualizadoHoje');
    if (diffDays === 1) return t('crmKanban.atualizadoOntem');
    return t('crmKanban.atualizadoHaDias', { dias: diffDays });
}

export function CrmBoardPicker({
    anchorEl, summaries, selectedBoardId, board, onClose, onSelect, onEdit, onDelete, onCreate, onToggleActive,
}: Props) {
    const { t } = useTranslation();
    const { canManageBoards } = useProfilePermissions();
    const [controller] = useMaterialUIController();
    const pc = pickerColors(useTheme(), controller);
    const [search, setSearch] = useState('');

    const filtered = summaries.filter((s) => s.name.toLowerCase().includes(search.trim().toLowerCase()));

    const close = () => {
        setSearch('');
        onClose();
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={close}
            autoFocus={false}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: pickerPaperSx(pc) } }}
        >
            <Typography sx={{ px: 2, py: 1.25, display: 'block', fontSize: 16, fontWeight: 700, color: pc.text }}>
                {t('crmKanban.trocarQuadro')}
            </Typography>

            <Box sx={{ px: 1.5, pb: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    // O Menu usa as letras para pular entre itens; aqui elas são da busca.
                    onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}
                    placeholder={t('crmKanban.buscarQuadro')}
                    sx={searchFieldSx(pc)}
                />
            </Box>

            <Divider sx={{ borderColor: pc.border }} />

            {filtered.length === 0 && (
                <Typography variant="caption" sx={{ px: 2, py: 1.5, display: 'block', color: pc.textSoft }}>
                    {t('general.nenhumRegistro')}
                </Typography>
            )}

            {filtered.map((s) => {
                const selected = s.id === selectedBoardId;
                const rowText = selected ? pc.white : pc.text;
                const rowTextSoft = selected ? alpha(pc.white, 0.8) : pc.textSoft;

                return (
                    <MenuItem
                        key={s.id}
                        selected={selected}
                        onClick={() => {
                            onSelect(s.id);
                            close();
                        }}
                        sx={boardItemSx(pc)}
                    >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                            <BackupTableIcon fontSize="small" sx={{ color: s.active ? rowText : rowTextSoft }} />
                        </ListItemIcon>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: rowText }}>
                                    {s.name}
                                </Typography>
                                {s.isDefault && (
                                    <Chip
                                        label={t('crmKanban.quadroPadrao')}
                                        size="small"
                                        sx={badgeSx(pc, DEFAULT_BADGE_TINT, selected, rowText)}
                                    />
                                )}
                                {/* Sem este selo, achar onde o lead novo cai exigiria abrir cada quadro. */}
                                {s.isIntake && (
                                    <Chip
                                        label={t('crmKanban.quadroEntradaSelo')}
                                        size="small"
                                        sx={badgeSx(pc, INTAKE_BADGE_TINT, selected, rowText)}
                                    />
                                )}
                                {!s.active && (
                                    <Chip label={t('crmKanban.fechado')} size="small" sx={{ fontSize: 10, height: 18, flexShrink: 0 }} />
                                )}
                            </Box>
                            <Typography variant="caption" noWrap sx={{ display: 'block', color: rowTextSoft }}>
                                {s.columnCount} {t('crmKanban.colunas', { count: s.columnCount })}
                                {' · '}{s.leadCount} {t('crmKanban.leads', { count: s.leadCount })}
                                {' · '}{formatRelativeUpdate(s.updatedAt, t)}
                            </Typography>
                        </Box>

                        {/* Grupo fixo: a legenda cresce com o número de dias e encostaria nos ícones. */}
                        {canManageBoards && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, ml: 2 }}>
                                <Tooltip title={t('general.editar')}>
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0.25 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(s);
                                            close();
                                        }}
                                    >
                                        <EditIcon fontSize="small" sx={{ color: rowTextSoft }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('crmKanban.excluirQuadro')}>
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0.25 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(s.id);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" color="error" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </MenuItem>
                );
            })}

            {canManageBoards && <Divider sx={{ borderColor: pc.border }} />}

            {canManageBoards && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
                    <MDButton
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            close();
                            onCreate();
                        }}
                        sx={createButtonSx(pc)}
                    >
                        {t('crmKanban.criarQuadro')}
                    </MDButton>

                    {/* Age sobre o quadro aberto, o marcado na lista acima. */}
                    {board && (
                        <MDButton
                            variant="text"
                            startIcon={board.active ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                            onClick={() => {
                                close();
                                onToggleActive();
                            }}
                            sx={toggleActiveButtonSx(pc, board.active)}
                        >
                            {board.active ? t('crmKanban.fecharQuadro') : t('crmKanban.reabrirQuadro')}
                        </MDButton>
                    )}
                </Box>
            )}
        </Menu>
    );
}
